import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import QuickActionPanel from '../../components/ui/QuickActionPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import CurrentSubscriptionCard from './components/CurrentSubscriptionCard';
import PlanComparisonTable from './components/PlanComparisonTable';
import BillingHistory from './components/BillingHistory';
import PaymentMethodManager from './components/PaymentMethodManager';
import UsageAnalytics from './components/UsageAnalytics';
import SubscriptionActions from './components/SubscriptionActions';

const SubscriptionManagementCenter = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [subscriptionData, setSubscriptionData] = useState(null);

  useEffect(() => {
    loadSubscriptionData();
  }, [userProfile?.id]);

  const loadSubscriptionData = async () => {
    setLoading(true);
    try {
      // Mock subscription data - in production, fetch from Supabase
      const mockData = {
        currentPlan: userProfile?.subscription_plan || 'professional',
        status: userProfile?.account_status || 'active',
        billingCycle: 'monthly',
        nextBillingDate: '2026-02-25',
        trialEndsAt: null,
        paymentMethod: userProfile?.payment_method || 'stripe',
        amount: getPlanAmount(userProfile?.subscription_plan),
        startDate: '2025-12-01',
        autoRenew: true
      };
      setSubscriptionData(mockData);
    } catch (error) {
      console.error('Error loading subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPlanAmount = (plan) => {
    const amounts = {
      starter: 49,
      professional: 149,
      enterprise: 399
    };
    return amounts?.[plan] || 0;
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'plans', label: 'Plans & Pricing', icon: 'Package' },
    { id: 'billing', label: 'Billing History', icon: 'Receipt' },
    { id: 'payment', label: 'Payment Methods', icon: 'CreditCard' },
    { id: 'usage', label: 'Usage Analytics', icon: 'BarChart3' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <RoleBasedHeader />
        <DashboardNavigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
            <p className="text-gray-600">Loading subscription data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Subscription Management Center | PulseLoom</title>
        <meta name="description" content="Manage your PulseLoom subscription, billing, and payment methods" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <RoleBasedHeader />
        <DashboardNavigation />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Subscription Management</h1>
                <p className="text-gray-600 mt-2">
                  Manage your platform subscription, billing, and payment preferences
                </p>
              </div>
              <Button
                variant="outline"
                iconName="HelpCircle"
                onClick={() => window.open('/ai-help-assistant', '_blank')}
              >
                Get Help
              </Button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="flex overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary bg-primary/5' :'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon name={tab?.icon} className="w-4 h-4" />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'overview' && (
              <>
                <CurrentSubscriptionCard subscriptionData={subscriptionData} />
                <SubscriptionActions subscriptionData={subscriptionData} onUpdate={loadSubscriptionData} />
                <UsageAnalytics subscriptionData={subscriptionData} />
              </>
            )}

            {activeTab === 'plans' && (
              <PlanComparisonTable currentPlan={subscriptionData?.currentPlan} onPlanChange={loadSubscriptionData} />
            )}

            {activeTab === 'billing' && (
              <BillingHistory subscriptionData={subscriptionData} />
            )}

            {activeTab === 'payment' && (
              <PaymentMethodManager 
                currentMethod={subscriptionData?.paymentMethod} 
                onUpdate={loadSubscriptionData}
              />
            )}

            {activeTab === 'usage' && (
              <UsageAnalytics subscriptionData={subscriptionData} detailed />
            )}
          </div>
        </div>

        <QuickActionPanel onAction={() => {}} />
      </div>
    </>
  );
};

export default SubscriptionManagementCenter;